import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, CardActions, Chip,
  CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, Grid, LinearProgress, Snackbar, Stack, TextField, Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import SendIcon from '@mui/icons-material/Send';
import HotelIcon from '@mui/icons-material/Hotel';
import DirectionsBusIcon from '@mui/icons-material/DirectionsBus';
import CardTravelIcon from '@mui/icons-material/CardTravel';
import LuggageIcon from '@mui/icons-material/Luggage';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import CampaignIcon from '@mui/icons-material/Campaign';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { intelligenceApi } from '../../api/intelligenceApi';

// ── Helpers ───────────────────────────────────────────────────────────────────

const TYPE_COLORS = {
  travel_tip: { bg: '#e3f2fd', text: '#1565c0', label: 'Travel Tip' },
  promotion:  { bg: '#fff3e0', text: '#e65100', label: 'Promotion'  },
  seasonal:   { bg: '#fce4ec', text: '#ad1457', label: 'Seasonal'   },
};

const CATEGORY_ICONS = {
  hotels:        <HotelIcon fontSize="small" />,
  packages:      <CardTravelIcon fontSize="small" />,
  accommodation: <LuggageIcon fontSize="small" />,
  vehicle_hire:  <DirectionsCarIcon fontSize="small" />,
  bus_booking:   <DirectionsBusIcon fontSize="small" />,
};

const STATUS_COLORS = {
  pending_approval: 'warning',
  approved:         'success',
  rejected:         'error',
  converted:        'default',
};

function categoryLabel(key) {
  return key ? key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : '—';
}

function openRatePct(rate) {
  if (rate === null || rate === undefined) return null;
  return `${(rate * 100).toFixed(1)}%`;
}

function timeAgo(iso) {
  if (!iso) return '—';
  const secs = Math.floor((Date.now() - new Date(iso)) / 1000);
  if (secs < 60)   return 'Just now';
  const mins = Math.floor(secs / 60);
  if (mins < 60)   return `${mins}m ago`;
  const hrs  = Math.floor(mins / 60);
  if (hrs  < 24)   return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

// ── Sub-components ────────────────────────────────────────────────────────────

/**
 * Builds a full email preview document (subject header + body + signature)
 * rendered inside an iframe srcDoc so it looks like a real inbox message.
 *
 * Uses the sender's real uploaded signature image when available.
 * Falls back to a clean text-based signature block if no image is set.
 */
function buildPreviewDoc(rec, sender = {}) {
  const subject      = rec.suggested_subject || '(No subject)';
  const body         = rec.draft_body_html   || '<p style="color:#999">No content generated.</p>';
  const senderName   = sender.name           || 'Abisiniya Travels';
  const senderEmail  = sender.email          || 'abisiniyatravels@gmail.com';
  const signatureUrl = sender.signature_url  || null;

  // Escape for safe HTML attribute/content insertion
  const esc = (s) => String(s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');

  const signatureBlock = signatureUrl
    ? /* Image signature from Settings */ `
      <div data-signature-marker="1"
           style="max-width:600px;margin:24px auto 0;padding:20px 24px 0;
                  font-family:Arial,sans-serif;border-top:2px solid #2db838;">
        <img src="${esc(signatureUrl)}"
             alt="Signature"
             style="display:block;max-width:320px;height:auto;border:none;" />
        <div style="margin-top:10px;font-size:11px;color:#aaa;border-top:1px solid #f0f0f0;padding-top:10px;">
          This email was sent by ${esc(senderName)} Marketing Automation.
          To unsubscribe, reply with &quot;Unsubscribe&quot; in the subject line.
        </div>
      </div>`
    : /* Text fallback */ `
      <div data-signature-marker="1"
           style="max-width:600px;margin:24px auto 0;padding:20px 24px 0;
                  font-family:Arial,sans-serif;border-top:2px solid #2db838;">
        <table cellpadding="0" cellspacing="0" style="width:100%">
          <tr>
            <td style="padding-right:14px;width:48px;vertical-align:middle">
              <div style="width:42px;height:42px;border-radius:50%;background:#2db838;
                          font-size:18px;font-weight:800;color:#fff;line-height:42px;
                          text-align:center;">${esc(senderName.charAt(0).toUpperCase())}</div>
            </td>
            <td style="vertical-align:middle">
              <div style="font-size:14px;font-weight:700;color:#111;margin-bottom:2px;">${esc(senderName)}</div>
              <div style="font-size:12px;color:#555;">Travel &amp; Tours | Zimbabwe</div>
              <div style="font-size:12px;margin-top:2px;">
                <a href="mailto:${esc(senderEmail)}"
                   style="color:#2db838;text-decoration:none;">${esc(senderEmail)}</a>
              </div>
            </td>
          </tr>
        </table>
        <div style="margin-top:12px;font-size:11px;color:#aaa;border-top:1px solid #f0f0f0;padding-top:10px;">
          This email was sent by ${esc(senderName)} Marketing Automation.
          To unsubscribe, reply with &quot;Unsubscribe&quot; in the subject line.
        </div>
      </div>`;

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width,initial-scale=1">
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { background: #f5f5f5; font-family: Arial, sans-serif; }
    .email-shell { max-width: 680px; margin: 0 auto; background: #fff; }
    .email-meta {
      background: #fff;
      border-bottom: 1px solid #e8e8e8;
      padding: 16px 24px 14px;
    }
    .email-meta .label { font-size: 11px; color: #999; text-transform: uppercase; letter-spacing: .06em; margin-bottom: 2px; }
    .subject-line { font-size: 18px; font-weight: 700; color: #111; line-height: 1.3; }
    .meta-row { display: flex; gap: 8px; align-items: baseline; margin-top: 8px; font-size: 12px; }
    .meta-row .key   { color: #999; min-width: 40px; }
    .meta-row .value { color: #333; }
    .email-body { padding: 0; }
  </style>
</head>
<body>
  <div class="email-shell">

    <!-- Email header: subject + from/to -->
    <div class="email-meta">
      <div class="label">Subject</div>
      <div class="subject-line">${esc(subject)}</div>
      <div class="meta-row">
        <span class="key">From:</span>
        <span class="value">${esc(senderName)} &lt;${esc(senderEmail)}&gt;</span>
      </div>
      <div class="meta-row">
        <span class="key">To:</span>
        <span class="value">Your subscribers</span>
      </div>
    </div>

    <!-- Email body -->
    <div class="email-body">
      ${body}
    </div>

    <!-- Signature (from account settings, or text fallback) -->
    ${signatureBlock}

  </div>
</body>
</html>`;
}

function ConflictAdvisoryBanner({ advisory }) {
  if (!advisory?.has_conflict) return null;

  const betterLabel = advisory.better_type
    ? categoryTypeLabel(advisory.better_type)
    : null;
  const betterRate = advisory.better_type
    ? openRatePct(advisory.rates?.[advisory.better_type])
    : null;
  const otherType  = advisory.better_type === 'travel_tip' ? 'promotion' : 'travel_tip';
  const otherRate  = openRatePct(advisory.rates?.[otherType]);

  return (
    <Alert severity="warning" sx={{ mb: 2 }}>
      <strong>Both a Travel Tip and a Promotion are awaiting review this week.</strong>{' '}
      Sending both in the same week is not recommended — contacts who receive the first will be
      automatically skipped for the second, which reduces total reach.
      {betterLabel && (
        <>
          {' '}Based on past results, <strong>{betterLabel}</strong> emails perform better
          {betterRate && ` (${betterRate} open rate vs ${otherRate ?? '—'})`}.
          {' '}Consider approving that one first this week.
        </>
      )}
    </Alert>
  );
}

function categoryTypeLabel(type) {
  return type === 'travel_tip' ? 'Travel Tip' : 'Promotion';
}

function EngagementBar({ rates }) {
  const types = ['travel_tip', 'promotion'];
  const hasAny = types.some((t) => rates[t] !== null && rates[t] !== undefined);

  if (!hasAny) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        No engagement data yet — stats will appear after the first automated campaign is sent.
      </Typography>
    );
  }

  return (
    <Stack spacing={1.5}>
      {types.map((type) => {
        const rate = rates[type];
        const pct  = rate !== null && rate !== undefined ? rate * 100 : null;
        const colors = TYPE_COLORS[type];
        return (
          <Box key={type}>
            <Stack direction="row" justifyContent="space-between" mb={0.5}>
              <Typography variant="body2" fontWeight={600} color={colors.text}>
                {colors.label}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {pct !== null ? `${pct.toFixed(1)}% open rate` : 'No data yet'}
              </Typography>
            </Stack>
            <LinearProgress
              variant={pct !== null ? 'determinate' : 'indeterminate'}
              value={pct !== null ? Math.min(100, pct) : undefined}
              sx={{
                height: 8,
                borderRadius: 4,
                bgcolor: '#f5f5f5',
                '& .MuiLinearProgress-bar': { bgcolor: colors.text },
              }}
            />
          </Box>
        );
      })}
    </Stack>
  );
}

function ConvertDialog({ open, recommendation, onClose, onConvert, loading }) {
  const [scheduleAt, setScheduleAt] = useState('');
  const [subject, setSubject]       = useState('');

  useEffect(() => {
    if (recommendation) {
      setSubject(recommendation.suggested_subject || '');
      // Default to suggested_send_date or 2 days from now at 10:00
      const suggested = recommendation.suggested_send_date
        ? new Date(recommendation.suggested_send_date)
        : (() => { const d = new Date(); d.setDate(d.getDate() + 2); d.setHours(10, 0, 0, 0); return d; })();
      // datetime-local requires "YYYY-MM-DDTHH:mm"
      const pad = (n) => String(n).padStart(2, '0');
      setScheduleAt(
        `${suggested.getFullYear()}-${pad(suggested.getMonth() + 1)}-${pad(suggested.getDate())}T${pad(suggested.getHours())}:${pad(suggested.getMinutes())}`
      );
    }
  }, [recommendation]);

  const handleSubmit = () => {
    if (!scheduleAt) return;
    onConvert(recommendation.id, new Date(scheduleAt).toISOString(), subject || null);
  };

  if (!recommendation) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Schedule Campaign — {categoryLabel(recommendation.content_theme)}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          <TextField
            label="Subject line"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            fullWidth
            size="small"
            helperText="Edit the AI-suggested subject if needed (max 55 characters)"
            inputProps={{ maxLength: 55 }}
          />
          <TextField
            label="Send date & time"
            type="datetime-local"
            value={scheduleAt}
            onChange={(e) => setScheduleAt(e.target.value)}
            fullWidth
            size="small"
            InputLabelProps={{ shrink: true }}
            helperText="Harare time (Africa/Harare). Contacts already emailed this week will be auto-skipped."
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button
          variant="contained"
          startIcon={loading ? <CircularProgress size={16} /> : <SendIcon />}
          onClick={handleSubmit}
          disabled={loading || !scheduleAt}
          sx={{ bgcolor: '#2db838', '&:hover': { bgcolor: '#259a2f' } }}
        >
          Schedule Campaign
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function RecommendationCard({ rec, sender, onApprove, onReject, onConvert, actioning }) {
  const [previewOpen, setPreviewOpen] = useState(false);
  const colors = TYPE_COLORS[rec.campaign_type] || { bg: '#f5f5f5', text: '#333', label: rec.campaign_type };
  const icon   = CATEGORY_ICONS[rec.content_theme];

  return (
    <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flexGrow: 1 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1}>
          <Stack direction="row" spacing={1} alignItems="center">
            {icon}
            <Typography variant="subtitle2" fontWeight={700}>
              {categoryLabel(rec.content_theme)}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={0.5}>
            <Chip
              label={colors.label}
              size="small"
              sx={{ bgcolor: colors.bg, color: colors.text, fontWeight: 700, fontSize: 11 }}
            />
            <Chip
              label={rec.status.replace('_', ' ')}
              size="small"
              color={STATUS_COLORS[rec.status] || 'default'}
              variant="outlined"
            />
          </Stack>
        </Stack>

        <Typography variant="body2" fontWeight={600} mb={0.5}>
          {rec.suggested_subject}
        </Typography>

        <Typography variant="caption" color="text.secondary" display="block" mb={1}>
          {rec.reason_for_recommendation}
        </Typography>

        <Stack direction="row" spacing={1} alignItems="center" mb={1}>
          <Typography variant="caption" color="text.secondary">
            AI Confidence:
          </Typography>
          <LinearProgress
            variant="determinate"
            value={rec.confidence_score}
            sx={{ flexGrow: 1, height: 6, borderRadius: 3, bgcolor: '#eee',
              '& .MuiLinearProgress-bar': { bgcolor: rec.confidence_score >= 70 ? '#2db838' : '#ff9800' } }}
          />
          <Typography variant="caption" color="text.secondary">
            {rec.confidence_score}%
          </Typography>
        </Stack>

        <Stack direction="row" justifyContent="space-between" alignItems="center" mt={0.5}>
          <Typography variant="caption" color="text.secondary">
            Generated {timeAgo(rec.created_at)}
          </Typography>
          <Button
            size="small"
            variant="outlined"
            sx={{ textTransform: 'none', fontSize: 11, py: 0.25, px: 1, borderColor: '#d0d0d0', color: '#555' }}
            onClick={() => setPreviewOpen(true)}
          >
            Preview HTML
          </Button>
        </Stack>

        {rec.campaign && (
          <Box mt={1.5} p={1} bgcolor="#f5f5f5" borderRadius={1}>
            <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
              Sent campaign result:
            </Typography>
            <Stack direction="row" spacing={2}>
              <Typography variant="caption">Sent: <strong>{rec.campaign.sent}</strong></Typography>
              <Typography variant="caption">Opened: <strong>{rec.campaign.open_rate}%</strong></Typography>
              <Typography variant="caption">Clicked: <strong>{rec.campaign.clicked}</strong></Typography>
            </Stack>
          </Box>
        )}

      </CardContent>

      {['pending_approval', 'approved'].includes(rec.status) && (
        <CardActions sx={{ px: 2, pb: 2, pt: 0, gap: 1 }}>
          {rec.status === 'pending_approval' && (
            <Button
              size="small"
              variant="outlined"
              color="success"
              startIcon={<CheckCircleOutlineIcon />}
              onClick={() => onApprove(rec.id)}
              disabled={actioning}
            >
              Approve
            </Button>
          )}
          {rec.status === 'pending_approval' && (
            <Button
              size="small"
              variant="outlined"
              color="error"
              startIcon={<CancelOutlinedIcon />}
              onClick={() => onReject(rec.id)}
              disabled={actioning}
            >
              Reject
            </Button>
          )}
          {rec.status === 'approved' && (
            <Button
              size="small"
              variant="contained"
              startIcon={<SendIcon />}
              onClick={() => onConvert(rec)}
              disabled={actioning}
              sx={{ bgcolor: '#2db838', '&:hover': { bgcolor: '#259a2f' } }}
            >
              Schedule Send
            </Button>
          )}
        </CardActions>
      )}

      {/* HTML Preview Dialog */}
      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 0 }}>
          Email Preview — {categoryLabel(rec.content_theme)}
        </DialogTitle>
        <DialogContent sx={{ pt: 1.5 }}>
          <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, overflow: 'hidden', bgcolor: '#f5f5f5' }}>
            <iframe
              title="Email Preview"
              srcDoc={buildPreviewDoc(rec, sender)}
              style={{ width: '100%', height: 620, border: 'none', display: 'block' }}
              sandbox="allow-same-origin"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}

// ── Main Panel ────────────────────────────────────────────────────────────────

export default function WeeklyContentPanel() {
  const [data, setData]               = useState(null);
  const [loading, setLoading]         = useState(true);
  const [actioning, setActioning]     = useState(false);
  const [snackbar, setSnackbar]       = useState(null);
  const [convertTarget, setConvertTarget] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    intelligenceApi.getWeeklyContent()
      .then(setData)
      .catch(() => setSnackbar({ msg: 'Failed to load weekly content.', severity: 'error' }))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleApprove = async (id) => {
    setActioning(true);
    try {
      await intelligenceApi.approveWeeklyContent(id);
      setSnackbar({ msg: 'Approved — now click "Schedule Send" to convert it to a campaign.', severity: 'success' });
      load();
    } catch {
      setSnackbar({ msg: 'Failed to approve.', severity: 'error' });
    } finally {
      setActioning(false);
    }
  };

  const handleReject = async (id) => {
    setActioning(true);
    try {
      await intelligenceApi.rejectWeeklyContent(id);
      setSnackbar({ msg: 'Rejected.', severity: 'info' });
      load();
    } catch {
      setSnackbar({ msg: 'Failed to reject.', severity: 'error' });
    } finally {
      setActioning(false);
    }
  };

  const handleConvert = async (id, scheduleAt, subject) => {
    setActioning(true);
    try {
      const res = await intelligenceApi.convertWeeklyContent(id, scheduleAt, subject);
      setSnackbar({
        msg: `Campaign scheduled for ${new Date(res.schedule_at).toLocaleString()}. Contacts already emailed this week will be auto-skipped.`,
        severity: 'success',
      });
      setConvertTarget(null);
      load();
    } catch {
      setSnackbar({ msg: 'Failed to schedule campaign.', severity: 'error' });
    } finally {
      setActioning(false);
    }
  };

  if (loading) {
    return <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress /></Box>;
  }

  const recommendations  = data?.recommendations ?? [];
  const travelTips       = recommendations.filter((r) => r.campaign_type === 'travel_tip');
  const promotions       = recommendations.filter((r) => r.campaign_type === 'promotion');
  const seasonal         = recommendations.filter((r) => r.campaign_type === 'seasonal');
  const engRates         = data?.engagement_rates ?? {};
  const advisory         = data?.conflict_advisory ?? {};
  const recentTouchDays  = data?.recent_touch_days ?? 7;

  // Sender info from the API — used in the email preview (signature + From field)
  const sender = {
    name:          data?.sender_name          ?? 'Abisiniya Travels',
    email:         data?.sender_email         ?? 'abisiniyatravels@gmail.com',
    signature_url: data?.sender_signature_url ?? null,
  };

  return (
    <Box>
      {/* Conflict Advisory */}
      <ConflictAdvisoryBanner advisory={advisory} />

      {/* Engagement Comparison */}
      <Card variant="outlined" sx={{ mb: 3, p: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
          <TrendingUpIcon color="action" fontSize="small" />
          <Typography variant="subtitle2" fontWeight={700}>
            Engagement Performance (last 8 weeks)
          </Typography>
        </Stack>
        <EngagementBar rates={engRates} />
        <Typography variant="caption" color="text.secondary" mt={1} display="block">
          The system uses this data to advise which type performs better when both are pending the same week.
          Anti-spam: contacts receive at most one automated email every{' '}
          <strong>{recentTouchDays} days</strong> (configurable via{' '}
          <code>BROADCAST_RECENT_TOUCH_DAYS</code> in <code>.env</code>).
        </Typography>
      </Card>

      {/* Two-column grid: Travel Tips | Promotions */}
      <Grid container spacing={3}>
        {/* Travel Tips */}
        <Grid item xs={12} md={6}>
          <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
            <LightbulbIcon sx={{ color: '#1565c0' }} />
            <Typography variant="h6" fontWeight={700} color="#1565c0">
              Travel Tips
            </Typography>
            <Chip
              label={travelTips.filter((r) => r.status === 'pending_approval').length + ' pending'}
              size="small"
              color="primary"
              variant="outlined"
            />
          </Stack>
          {travelTips.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No travel tip recommendations yet. They are generated every Monday at 07:00.
            </Typography>
          ) : (
            <Stack spacing={2}>
              {travelTips.map((rec) => (
                <RecommendationCard
                  key={rec.id}
                  rec={rec}
                  sender={sender}
                  onApprove={handleApprove}
                  onReject={handleReject}
                  onConvert={setConvertTarget}
                  actioning={actioning}
                />
              ))}
            </Stack>
          )}
        </Grid>

        {/* Promotions */}
        <Grid item xs={12} md={6}>
          <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
            <CampaignIcon sx={{ color: '#e65100' }} />
            <Typography variant="h6" fontWeight={700} color="#e65100">
              Promotions
            </Typography>
            <Chip
              label={promotions.filter((r) => r.status === 'pending_approval').length + ' pending'}
              size="small"
              color="warning"
              variant="outlined"
            />
          </Stack>
          {promotions.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No promotion recommendations yet. They are generated every Monday at 07:15.
            </Typography>
          ) : (
            <Stack spacing={2}>
              {promotions.map((rec) => (
                <RecommendationCard
                  key={rec.id}
                  rec={rec}
                  sender={sender}
                  onApprove={handleApprove}
                  onReject={handleReject}
                  onConvert={setConvertTarget}
                  actioning={actioning}
                />
              ))}
            </Stack>
          )}
        </Grid>
      </Grid>

      {/* Seasonal Campaigns (occasion-triggered) */}
      {seasonal.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
            <CampaignIcon sx={{ color: '#ad1457' }} />
            <Typography variant="h6" fontWeight={700} color="#ad1457">
              Seasonal Campaigns
            </Typography>
            <Chip
              label={seasonal.filter((r) => r.status === 'pending_approval').length + ' pending'}
              size="small"
              sx={{ borderColor: '#ad1457', color: '#ad1457' }}
              variant="outlined"
            />
          </Stack>
          <Stack spacing={2}>
            {seasonal.map((rec) => (
              <RecommendationCard
                key={rec.id}
                rec={rec}
                sender={sender}
                onApprove={handleApprove}
                onReject={handleReject}
                onConvert={setConvertTarget}
                actioning={actioning}
              />
            ))}
          </Stack>
        </Box>
      )}

      {/* Convert / Schedule Dialog */}
      <ConvertDialog
        open={Boolean(convertTarget)}
        recommendation={convertTarget}
        onClose={() => setConvertTarget(null)}
        onConvert={handleConvert}
        loading={actioning}
      />

      {/* Snackbar */}
      <Snackbar
        open={Boolean(snackbar)}
        autoHideDuration={6000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar?.severity ?? 'info'} onClose={() => setSnackbar(null)}>
          {snackbar?.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
